import classNames from "classnames";
import { CSSProperties, forwardRef, useId, useMemo } from "react";
import { type FieldError } from "react-hook-form";

import { useSuppressBrowserAutocomplete } from "./suppress-browser-autocomplete";

export interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  isRequired?: boolean;
  validationState?: "success" | "error";
  containerClassName?: string;
  labelClassName?: string;
  //TODO: haven't done anything w/ this just yet well want to match style guidelines https://preview.jbecker.com/hlc3/components-forms.html
  error?: FieldError;
  // serverValidationError?: any;
  // TODO: we need to remove col class from all form so we can handle this in a container higher up
  disableColClass?: boolean;
  addOnClassName?: string;
  hideErrorMessage?: boolean;
}

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      isRequired = false,
      validationState,
      containerClassName,
      labelClassName,
      error,
      disableColClass = false,
      autoComplete,
      addOnClassName = null,
      hideErrorMessage = false,
      readOnly,
      onFocus,
      onMouseDown,
      type,
      ...inputProps
    },
    ref
  ) => {
    const inputId = useId();
    const browserAutocompleteSuppression = useSuppressBrowserAutocomplete(autoComplete, {
      readOnly,
      type,
      onFocus,
      onMouseDown
    });

    const style = useMemo(() => {
      const results: CSSProperties = {};
      if (!label) {
        results["paddingTop"] = "0.625rem";
      }
      if (!!addOnClassName) {
        results["borderTopRightRadius"] = "0.5rem";
        results["borderBottomRightRadius"] = "0.5rem";
      }
      return results;
    }, [label, addOnClassName]);

    return (
      <div
        className={classNames(
          containerClassName,
          { "form-floating": !!label, "input-group": !!addOnClassName },
          { col: !disableColClass }
        )}
      >
        {addOnClassName && (
          <span data-testid={`${inputId}-add-on`} className="input-group-text">
            <i className={addOnClassName}></i>
          </span>
        )}
        <input
          {...inputProps}
          id={inputId}
          type={type}
          aria-invalid={validationState === "error" || error?.message ? "true" : "false"}
          className={classNames("form-control", inputProps.className, {
            "is-valid": validationState === "success",
            "is-invalid": validationState === "error" || error?.message
          })}
          ref={ref}
          readOnly={browserAutocompleteSuppression.readOnly}
          onFocus={browserAutocompleteSuppression.onFocus}
          onMouseDown={browserAutocompleteSuppression.onMouseDown}
          autoComplete={browserAutocompleteSuppression.autoComplete}
          autoCorrect={browserAutocompleteSuppression.autoCorrect}
          spellCheck={browserAutocompleteSuppression.spellCheck}
          data-1p-ignore={browserAutocompleteSuppression["data-1p-ignore"]}
          data-lpignore={browserAutocompleteSuppression["data-lpignore"]}
          style={style}
        />
        {label && (
          <label htmlFor={inputId} className={classNames(labelClassName, "text-nowrap")}>
            {label}
            {isRequired && (
              <>
                {" "}
                <span className="required" aria-hidden="true">
                  *
                </span>
              </>
            )}
          </label>
        )}
        {!hideErrorMessage && !!error?.message && <div className="invalid-feedback"> {error.message}</div>}
      </div>
    );
  }
);

InputField.displayName = "TextField";
